import {
  useMemo,
  useState,
  type ReactNode,
  type Key,
} from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../utils/cn.js';
import { Button } from './Button.js';
import { EmptyState } from './EmptyState.js';
import { Notice } from './Notice.js';
import { Drawer } from './Drawer.js';

/**
 * ListPage — React port of `KIT.list(config)` from kollez.ai's static kit.js.
 * One primitive consumes ~40 pages across the app (drives, students, opportunities, offers,
 * partners, tenants, MoUs, compliance, invoices, notifications, etc.).
 *
 * Contract mirrors kit.js verbatim so v15 static screens can be ported line-for-line.
 */

export interface ListPageAction {
  label: ReactNode;
  icon?: ReactNode;
  href?: string;
  primary?: boolean;
  onClick?: () => void;
}

export interface ListPageKpi {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  tone?: 'default' | 'accent' | 'success' | 'warning' | 'danger' | 'info';
  href?: string;
}

export interface ListPageColumn<Row> {
  key: string;
  label: ReactNode;
  render: (row: Row) => ReactNode;
  hideMobile?: boolean;
  right?: boolean;
  className?: string;
}

export interface ListPageBulkAction<Row> {
  label: ReactNode;
  icon?: ReactNode;
  run: (selected: Row[], api: { redraw: () => void; toast?: (m: string) => void }) => void;
}

export interface ListPageDrawer<Row> {
  eyebrow?: ReactNode;
  title: ReactNode;
  body: ReactNode;
  foot?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
}

export interface ListPageProps<Row> {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ListPageAction[];

  notice?: {
    tone?: 'info' | 'warning' | 'success';
    icon?: ReactNode;
    children: ReactNode;
    action?: { label: string; onClick: () => void };
  };

  kpis?: ListPageKpi[];

  segment?: {
    key: keyof Row & string;
    options: Array<[value: string, label: ReactNode]>;
    /** First option is "All" — treated as no-op filter if value === '' or 'all'. */
    defaultValue?: string;
  };

  filters?: Array<{
    key: keyof Row & string;
    label: string;
    options: Array<string | number>;
  }>;

  search?: {
    placeholder?: string;
    keys: Array<keyof Row & string>;
  };

  columns: Array<ListPageColumn<Row>>;
  rows: Row[];
  rowKey: (row: Row) => Key;
  rowSub?: (row: Row) => ReactNode;
  drawer?: (row: Row) => ListPageDrawer<Row>;
  bulk?: Array<ListPageBulkAction<Row>>;

  total?: string;
  empty?: {
    title: string;
    hint?: ReactNode;
    action?: ReactNode;
  };
  after?: ReactNode;
  /** Loading + error state — when set, the table area shows a skeleton or error banner. */
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
}

/**
 * Full-page list — handles header, KPIs, filter bar, chips, table + row drawer, bulk bar.
 * `<AppShell>` wraps this. Non-app-shell (public) pages don't use ListPage.
 */
export function ListPage<Row>(props: ListPageProps<Row>) {
  const {
    eyebrow, title, subtitle, actions,
    notice, kpis, segment, filters = [], search,
    columns, rows, rowKey, rowSub, drawer, bulk,
    total, empty, after,
    loading: _loading, error: _error, onRetry: _onRetry,
  } = props;
  void _loading; void _error; void _onRetry;

  const [segVal, setSegVal] = useState<string>(
    segment?.defaultValue ?? (segment?.options[0]?.[0] as string) ?? '',
  );
  const [q, setQ] = useState('');
  const [f, setF] = useState<Record<string, string>>({});
  const [sel, setSel] = useState<Set<Key>>(new Set());
  const [openRow, setOpenRow] = useState<Row | null>(null);

  const qLc = q.trim().toLowerCase();

  const visible = useMemo<Row[]>(() => {
    return rows.filter((r) => {
      if (segment && segVal && segVal !== 'all') {
        const rv = (r as Record<string, unknown>)[segment.key];
        if (rv !== segVal) return false;
      }
      for (const key of Object.keys(f)) {
        if (!f[key]) continue;
        const rv = (r as Record<string, unknown>)[key];
        if (String(rv) !== f[key]) return false;
      }
      if (qLc && search) {
        const hay = search.keys
          .map((k) => String((r as Record<string, unknown>)[k] ?? ''))
          .join(' ')
          .toLowerCase();
        if (!hay.includes(qLc)) return false;
      }
      return true;
    });
  }, [rows, segment, segVal, f, qLc, search]);

  const activeChips = Object.entries(f).filter(([, v]) => v);

  const selectedRows = useMemo(() => rows.filter((r) => sel.has(rowKey(r))), [rows, sel, rowKey]);
  const selectedCount = selectedRows.length;

  const resetFilters = () => {
    setF({});
    setQ('');
  };

  return (
    <div className="stack">
      {/* Header */}
      <div className="page-header">
        <div className="ph-text">
          {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
          <h1 className="h-page">{title}</h1>
          {subtitle ? <p className="subhead">{subtitle}</p> : null}
        </div>
        {actions && actions.length > 0 ? (
          <div className="ph-actions">
            {actions.map((a, i) => {
              const cls = cn('btn', a.primary ? 'btn-primary' : 'btn-outline');
              const content = <>{a.icon}{a.label}</>;
              if (a.href) {
                return <a key={i} className={cls} href={a.href}>{content}</a>;
              }
              return <button key={i} type="button" className={cls} onClick={a.onClick}>{content}</button>;
            })}
          </div>
        ) : null}
      </div>

      {/* Notice */}
      {notice ? (
        <Notice tone={notice.tone ?? 'info'}>
          <span className="flex-1">{notice.children}</span>
          {notice.action ? (
            <Button size="sm" variant="secondary" onClick={notice.action.onClick}>
              {notice.action.label}
            </Button>
          ) : null}
        </Notice>
      ) : null}

      {/* KPIs */}
      {kpis && kpis.length > 0 ? (
        <div className={cn('grid gap-3 grid-cols-2', kpis.length >= 5 ? 'lg:grid-cols-5' : `lg:grid-cols-${kpis.length}`)}>
          {kpis.map((k, i) => {
            const inner = (
              <>
                <span className="kpi-label">{k.label}</span>
                <span className="kpi-value">{k.value}</span>
                {k.meta ? <span className="kpi-meta">{k.meta}</span> : null}
              </>
            );
            const props = { className: 'card kpi', 'data-tone': k.tone === 'default' ? undefined : k.tone };
            return k.href
              ? <a key={i} href={k.href} {...props}>{inner}</a>
              : <div key={i} {...props}>{inner}</div>;
          })}
        </div>
      ) : null}

      {/* Filter bar + table */}
      <section className="card">
        <div className="filterbar">
          {search ? (
            <div className="input-group fb-search">
              <Search size={14} aria-hidden />
              <input
                className="input"
                placeholder={search.placeholder ?? 'Search'}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          ) : null}

          {segment ? (
            <div className="segmented" role="group">
              {segment.options.map(([v, label]) => {
                const active = v === segVal;
                return (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setSegVal(v)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          ) : null}

          {filters.map((fl) => (
            <select
              key={fl.key}
              className="select"
              aria-label={fl.label}
              value={f[fl.key] ?? ''}
              onChange={(e) => setF((prev) => ({ ...prev, [fl.key]: e.target.value }))}
            >
              <option value="">{fl.label}</option>
              {fl.options.map((o) => <option key={String(o)} value={String(o)}>{o}</option>)}
            </select>
          ))}

          <span className="fb-count">
            {visible.length} shown{total ? ` ${total}` : ''}
          </span>
        </div>

        {/* Active-filter chips */}
        {activeChips.length > 0 ? (
          <div className="chips-row">
            {activeChips.map(([key, val]) => (
              <span key={key} className="chip">
                {val}
                <button
                  type="button"
                  aria-label="Remove filter"
                  onClick={() => setF((prev) => ({ ...prev, [key]: '' }))}
                >
                  <X size={12} aria-hidden />
                </button>
              </span>
            ))}
            <button type="button" className="link text-sm ml-1" onClick={resetFilters}>
              Clear all
            </button>
          </div>
        ) : null}

        {/* Rows */}
        <div className="tbl-wrap">
          {visible.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title={empty?.title ?? 'Nothing here yet'}
                hint={empty?.hint}
                action={empty?.action ?? (activeChips.length + (q ? 1 : 0) > 0 ? (
                  <Button variant="secondary" size="sm" onClick={resetFilters}>Clear filters</Button>
                ) : undefined)}
              />
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  {bulk ? (
                    <th className="w-check">
                      <input
                        type="checkbox"
                        className="check"
                        aria-label="Select all"
                        checked={visible.length > 0 && visible.every((r) => sel.has(rowKey(r)))}
                        onChange={(e) => {
                          setSel((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) visible.forEach((r) => next.add(rowKey(r)));
                            else visible.forEach((r) => next.delete(rowKey(r)));
                            return next;
                          });
                        }}
                      />
                    </th>
                  ) : null}
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      className={cn(c.hideMobile && 'hide-mobile', c.right && 'r', c.className)}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const id = rowKey(row);
                  const isSelected = sel.has(id);
                  return (
                    <tr
                      key={String(id)}
                      className={drawer ? 'clickable' : undefined}
                      tabIndex={drawer ? 0 : undefined}
                      aria-selected={isSelected}
                      onClick={(e) => {
                        // ignore clicks on interactive descendants
                        if ((e.target as HTMLElement).closest('input,button,a,select')) return;
                        if (drawer) setOpenRow(row);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && drawer) setOpenRow(row);
                      }}
                    >
                      {bulk ? (
                        <td className="w-check">
                          <input
                            type="checkbox"
                            className="check"
                            aria-label="Select"
                            checked={isSelected}
                            onChange={(e) => {
                              setSel((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(id); else next.delete(id);
                                return next;
                              });
                            }}
                          />
                        </td>
                      ) : null}
                      {columns.map((c, i) => (
                        <td
                          key={c.key}
                          className={cn(c.hideMobile && 'hide-mobile', c.right && 'r', c.className)}
                        >
                          {c.render(row)}
                          {i === 0 && rowSub ? (
                            <div className="cell-sub show-mobile">{rowSub(row)}</div>
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {after}

      {/* Row drawer */}
      {drawer && openRow ? (
        (() => {
          const d = drawer(openRow);
          return (
            <Drawer
              open={!!openRow}
              onOpenChange={(o) => !o && setOpenRow(null)}
              title={d.title}
              description={d.eyebrow ? <span className="eyebrow">{d.eyebrow}</span> : undefined}
              size={d.size ?? 'md'}
              footer={d.foot}
            >
              {d.body}
            </Drawer>
          );
        })()
      ) : null}

      {/* Bulk bar */}
      {bulk && selectedCount > 0 ? (
        <div className="bulkbar" data-open="true">
          <span className="text-sm whitespace-nowrap">
            <b>{selectedCount}</b> selected
          </span>
          {bulk.map((b, i) => (
            <button
              key={i}
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() =>
                b.run(selectedRows, { redraw: () => { /* re-renders on state */ } })
              }
            >
              {b.icon}
              {b.label}
            </button>
          ))}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setSel(new Set())}
          >
            Clear
          </button>
        </div>
      ) : null}
    </div>
  );
}
